import sampleClassLogo from "assets/img/rectangle-logo-v2.png";
import { LOCAL_MOCK_SAMPLE_CLASS_ID } from "./localMockSampleClassConstants";

export const GETTING_STARTED_CLASS_ID = "getting-started";
export const GETTING_STARTED_CLASS_NAME = "Getting Started";
export const GETTING_STARTED_ONBOARDING_BOX_ID = "gettingStartedOnboardingBox";
export const GETTING_STARTED_RECORDINGS_BOX_ID = "gettingStartedRecordingsBox";
export const LEGACY_SAMPLE_CLASS_ID = "welcome1";
export const LEGACY_SAMPLE_CLASS_NAME = "Sample Class";

const BLANK_SCHEDULE_WEEKS = ["A", "B", "C", "D", "E", "F", "G", "H", "I", "J", "K", "L", "M", "N"];
const BLANK_SCHEDULE_DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

const buildBlankSchedule = () =>
  BLANK_SCHEDULE_WEEKS.reduce((schedule, weekKey) => ({
    ...schedule,
    [weekKey]: BLANK_SCHEDULE_DAYS.reduce((weekSchedule, dayName) => ({
      ...weekSchedule,
      [dayName]: {
        active: !["Saturday", "Sunday"].includes(dayName),
        startTime: "00:00",
        endTime: "00:00",
      },
    }), {}),
  }), {});

export function createGettingStartedOnboardingBox(
  contentBoxId = GETTING_STARTED_ONBOARDING_BOX_ID
) {
  return {
    id: contentBoxId,
    contentBoxId,
    sampleClassOnboardingBox: true,
    heading: "Onboarding Steps",
    show: true,
    active: true,
    startDate: "2023-10-01",
    endDate: "2099-12-31",
  };
}

export function createGettingStartedRecordingsBox(
  contentBoxId = GETTING_STARTED_RECORDINGS_BOX_ID
) {
  return {
    id: contentBoxId,
    contentBoxId,
    sampleClassRecordingsBox: true,
    heading: "Help Videos",
    show: true,
    active: true,
    startDate: "2023-10-01",
    endDate: "2099-12-31",
  };
}

export const createGettingStartedContentBoxes = () => [
  createGettingStartedOnboardingBox(),
  createGettingStartedRecordingsBox(),
];

export const GETTING_STARTED_CONTENT_BOXES = createGettingStartedContentBoxes();

export const GETTING_STARTED_WELCOME_ANNOUNCEMENT_TEXT = "Welcome to Lesson Launchpad";
export const GETTING_STARTED_WELCOME_ANNOUNCEMENT_COLOR = "rgb(0, 74, 173)";
export const GETTING_STARTED_WELCOME_ANNOUNCEMENT_HTML =
  `<p><strong><span style="color: ${GETTING_STARTED_WELCOME_ANNOUNCEMENT_COLOR};">${GETTING_STARTED_WELCOME_ANNOUNCEMENT_TEXT}</span></strong></p>`;

export function withGettingStartedWelcomeMarqueeColor(announcement) {
  if (!announcement || typeof announcement !== "object") {
    return announcement;
  }

  const html = String(announcement.html || "");
  const nextHtml = html.replace(
    /rgb\(\s*11\s*,\s*192\s*,\s*223\s*\)|#0bc0df/gi,
    GETTING_STARTED_WELCOME_ANNOUNCEMENT_COLOR
  );
  if (nextHtml === html) {
    return announcement;
  }

  return {
    ...announcement,
    html: nextHtml,
  };
}

export function buildGettingStartedClassData() {
  return {
    classId: GETTING_STARTED_CLASS_ID,
    period: "",
    className: GETTING_STARTED_CLASS_NAME,
    startTime: null,
    endTime: null,
    showMarquee: true,
    showClock: true,
    active: true,
    schedule: buildBlankSchedule(),
    orderOfAnnouncements: ["id222222222"],
    contentBoxesOrder: GETTING_STARTED_CONTENT_BOXES.map((box) => box.contentBoxId),
    defaultAnnouncement: {
      mode: "always",
      announcement: {
        html: GETTING_STARTED_WELCOME_ANNOUNCEMENT_HTML,
        text: GETTING_STARTED_WELCOME_ANNOUNCEMENT_TEXT,
        active: true,
        show: true,
        startDate: ["Mondays", "Tuesdays", "Wednesdays", "Thursdays", "Fridays"],
        endDate: "",
        lastEdited: "",
        dateCreated: "",
      },
    },
    gettingStartedClass: true,
    hiddenFromClassList: false,
    showClassInfoSchedule: false,
    showClassInfoLogo: true,
    classInfoLogoUrl: sampleClassLogo,
    classInfoLogoStoragePath: "",
    contentBoxes: createGettingStartedContentBoxes(),
  };
}

export function isGettingStartedClass(classOrId) {
  const classId = typeof classOrId === "string" ? classOrId : classOrId?.classId;
  return classId === GETTING_STARTED_CLASS_ID || classOrId?.gettingStartedClass === true;
}

function getClassId(classOrId) {
  return typeof classOrId === "string" ? classOrId : classOrId?.classId;
}

/**
 * True for leftover Sample Class docs that still look like the old welcome1 demo.
 * New accounts get Getting Started instead. Teachers who renamed welcome1 into a
 * real period should get normal copy, link, and Content Manager behavior.
 */
export function isUnmodifiedLegacySampleClass(classOrId) {
  if (getClassId(classOrId) !== LEGACY_SAMPLE_CLASS_ID) {
    return false;
  }

  if (typeof classOrId === "string") {
    return true;
  }

  const className = String(classOrId?.className || "").trim();
  return !className || className === LEGACY_SAMPLE_CLASS_NAME;
}

export function isSystemOnboardingClass(classOrId) {
  const classId = getClassId(classOrId);
  return (
    classId === GETTING_STARTED_CLASS_ID
    || classId === LOCAL_MOCK_SAMPLE_CLASS_ID
    || classOrId?.gettingStartedClass === true
    || classOrId?.localMockSampleClass === true
    || isUnmodifiedLegacySampleClass(classOrId)
  );
}

export function isGettingStartedHidden(classData = {}) {
  return isGettingStartedClass(classData) && classData.hiddenFromClassList === true;
}

export function canHideGettingStarted(classes = []) {
  return (classes || []).some((classItem) => !isSystemOnboardingClass(classItem));
}

export function getGettingStartedContentBoxes(classId) {
  if (classId !== GETTING_STARTED_CLASS_ID) {
    return null;
  }

  return createGettingStartedContentBoxes();
}
